<template>
    <div class="unit-type-item-modal">
        <section v-if="showSaveAsAnotherUserWarning" class="mt-8 mb-10 text-bold">
            <p>
                {{
                    $t('ui.warning_perform_action_as_oem', { oem: userStore.userData.owner_email })
                }}
            </p>
        </section>

        <section v-else>
            <div v-if="tab < 1">
                <InputsBasicInfo />
                <InputsPurchaseInfo />
            </div>

            <div v-else>
                <InputsUtilizationInfo />
                <InputsBuyBackOption />
                <Transition name="rv-expand">
                    <InputsResidualValue v-if="unitTypeStore.unitTypeData.buy_back" class="mt-8" />
                </Transition>
            </div>
        </section>

        <div class="flex justify-start gap-2 mt-5">
            <CoreLinkPrimary
                v-if="tab >= 1 && !showSaveAsAnotherUserWarning"
                text="§ui_button.back"
                class="unit-type-item-modal__back-button"
                @click="back"
            />

            <CoreButton
                v-if="showNextButton"
                :disabled="isNextButtonDisabled"
                label="§ui_button.continue"
                @click="next"
            />

            <CoreButton
                v-if="showSaveButton"
                :disabled="isSaveButtonDisabled"
                label="§ui_button.save"
                icon="pi-save"
                :loading="unitTypeStore.saving"
                @click="save"
            />

            <CoreButton :label="cancelButtonLabel" variant="tertiary" @click="handleClose" />
        </div>
    </div>
</template>

<script>
import { useCafStore } from '@/stores/caf'
import { useModalStore } from '@/stores/modal'
import { useUnitTypeStore } from '@/stores/unitType'
import { useUserStore } from '@/stores/user'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreLinkPrimary from '@/components/core/CoreLinkPrimary.vue'

import InputsBasicInfo from './components/InputsBasicInfo.vue'
import InputsPurchaseInfo from './components/InputsPurchaseInfo.vue'
import InputsUtilizationInfo from './components/InputsUtilizationInfo.vue'
import InputsBuyBackOption from './components/InputsBuyBackOption.vue'
import InputsResidualValue from '@/views/UnitType/components/InputsResidualValue.vue'

export default {
    name: 'UnitTypeCreateModal',

    components: {
        CoreButton,
        CoreLinkPrimary,
        InputsResidualValue,
        InputsBasicInfo,
        InputsPurchaseInfo,
        InputsUtilizationInfo,
        InputsBuyBackOption,
    },

    data() {
        return {
            tab: 0,

            isDetailsPageValid: false,
            isUsagePageValid: false,

            cafStore: useCafStore(),
            modalStore: useModalStore(),
            unitTypeStore: useUnitTypeStore(),
            userStore: useUserStore(),

            showSaveAsAnotherUserWarning: false,
        }
    },

    computed: {
        showNextButton() {
            return this.tab === 0
        },

        isNextButtonDisabled() {
            return !this.unitTypeStore.isValidBasicInfo || !this.unitTypeStore.isValidPurchaseInfo
        },

        showSaveButton() {
            return this.tab === 1
        },

        isSaveButtonDisabled() {
            if (!this.unitTypeStore.isValidUtilizationInfo) {
                return true
            }

            if (this.unitTypeStore.unitTypeData.buy_back) {
                return (
                    !this.unitTypeStore.isValidResidualValueInputs ||
                    !this.unitTypeStore.isRvtCurrent ||
                    this.unitTypeStore.rvtPendingRecalculation !== null ||
                    this.unitTypeStore.calculating
                )
            }

            return false
        },

        cancelButtonLabel() {
            if (this.showSaveAsAnotherUserWarning) {
                return '§ui_button.cancel_change_oem'
            }

            return '§ui_button.cancel'
        },
    },

    beforeUnmount() {
        this.unitTypeStore.resetState()
    },

    methods: {
        handleClose() {
            this.modalStore.setClose()
        },

        next() {
            this.tab += 1
        },

        back() {
            this.tab -= 1
        },

        async save() {
            if (this.isActAsOemActive() && !this.showSaveAsAnotherUserWarning) {
                this.showSaveAsAnotherUserWarning = true
                return
            }
            const FROM_CAF_MODAL = true
            await this.unitTypeStore.create(FROM_CAF_MODAL)

            this.cafStore.sharedInputs.unit_type = this.unitTypeStore.unitTypeData.id

            this.cafStore.sharedInputs.unit_type_name = this.unitTypeStore.unitTypeData.name
            this.cafStore.ppu.unit_type_max_usage =
                this.unitTypeStore.unitTypeData.maximum_usage_per_a
            this.cafStore.ppu.unit_type_planned_usage =
                this.unitTypeStore.unitTypeData.planned_usage_per_a
            this.cafStore.sharedInputs.currency_code = this.unitTypeStore.unitTypeData.currency_code

            const billingUnitKeys = Object.keys(this.unitTypeStore.unitTypeData.billing_units)

            if (billingUnitKeys.length > 0) {
                const billingUnits = Object.keys(this.unitTypeStore.unitTypeData.billing_units).map(
                    (label) => ({
                        label,
                        value: this.unitTypeStore.unitTypeData.billing_units[label],
                    })
                )
                this.cafStore.ppu.unit_type_billing_units = billingUnits
                this.cafStore.ppu.billing_unit = billingUnits.at(0).value
            }
            this.cafStore.sharedInputs.net_list_price = this.unitTypeStore.unitTypeData.value

            if (
                this.cafStore.ppu.expected_utilization === null &&
                !this.cafStore.ppu.expected_utilization_by_month.length
            ) {
                this.cafStore.ppu.expected_utilization =
                    this.unitTypeStore.unitTypeData.planned_usage_per_a
            }

            this.handleClose()
        },
    },
}
</script>
